import { createContext, useContext, useEffect } from 'react'
import { Link, NavLink, Navigate, Outlet, useParams } from 'react-router'
import { get } from '../../api'
import { useAuth } from '../../auth'
import { Alert, Spinner } from '../../components/ui'
import { useLoad, type Loaded } from '../../hooks'
import { initials } from '../../format'
import type { Business, Role } from '../../types'
import { rememberBusiness } from '../Home'

// The frame around every business screen: navigation plus the business record, loaded once and
// shared with the screens through context.

interface BusinessCtx {
  business: Business
  role: Role | 'platform'
  can: (atLeast: Role) => boolean
  reload: Loaded<Business>['reload']
  setBusiness: (b: Business) => void
}

const Ctx = createContext<BusinessCtx | null>(null)

export function useBusiness(): BusinessCtx {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useBusiness outside BusinessLayout')
  return ctx
}

const RANK: Record<Role, number> = { staff: 1, manager: 2, owner: 3 }

export function BusinessLayout() {
  const { businessId = '' } = useParams()
  const { me, signOut } = useAuth()
  const loaded = useLoad(() => get<Business>(`/v1/admin/businesses/${businessId}`), [businessId])
  useEffect(() => {
    if (businessId) rememberBusiness(businessId)
  }, [businessId])
  if (!me) return null
  const membership = me.memberships.find((m) => m.businessId === businessId)
  if (!membership && !me.user.isPlatformAdmin) return <Navigate to="/" replace />
  const role: Role | 'platform' = membership?.role ?? 'platform'
  const can = (atLeast: Role) => role === 'platform' || RANK[role] >= RANK[atLeast]

  const nav = (
    <>
      <NavLink to="today" className={({ isActive }) => `nav ${isActive ? 'active' : ''}`}>
        <TodayIcon /> Today
      </NavLink>
      <NavLink to="bookings" className={({ isActive }) => `nav ${isActive ? 'active' : ''}`}>
        <CalendarIcon /> Bookings
      </NavLink>
      <NavLink to="guests" className={({ isActive }) => `nav ${isActive ? 'active' : ''}`}>
        <GuestsIcon /> Guests
      </NavLink>
      <NavLink to="settings" className={({ isActive }) => `nav ${isActive ? 'active' : ''}`}>
        <SettingsIcon /> Settings
      </NavLink>
    </>
  )

  return (
    <div className="shell">
      <aside className="sidebar">
        <Link to="/" className="brand">
          <span className="brand-mark" /> Tabula
        </Link>
        {loaded.data ? (
          <div className="business-switch">
            <span className="name">{loaded.data.name}</span>
            {me.memberships.length > 1 || me.user.isPlatformAdmin ? <Link to="/">Switch restaurant</Link> : null}
          </div>
        ) : null}
        {nav}
        <div className="sidebar-foot stack">
          <div className="user-menu">
            <span className="avatar">{initials(me.user.name ?? me.user.email)}</span>
            <span className="grow" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {me.user.name ?? me.user.email}
            </span>
          </div>
          <div className="row-flex small">
            <Link to="/account">Account</Link>
            {me.user.isPlatformAdmin ? <Link to="/platform">Platform</Link> : null}
            <button className="link-btn" onClick={() => void signOut()}>
              Sign out
            </button>
          </div>
        </div>
      </aside>
      <main className="main">
        {loaded.loading && !loaded.data ? (
          <Spinner />
        ) : loaded.error && !loaded.data ? (
          <Alert>{loaded.error}</Alert>
        ) : loaded.data ? (
          <Ctx.Provider value={{ business: loaded.data, role, can, reload: loaded.reload, setBusiness: (b) => loaded.setData(() => b) }}>
            <Outlet />
          </Ctx.Provider>
        ) : null}
      </main>
      <nav className="tabbar" aria-label="Main">
        {nav}
      </nav>
    </div>
  )
}

function TodayIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  )
}
function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
  )
}
function GuestsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0M16 4.5a3.5 3.5 0 0 1 0 7M21.5 20a6.5 6.5 0 0 0-4.5-6.2" />
    </svg>
  )
}
function SettingsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </svg>
  )
}
